import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { AlertTriangle, Layers, Lock, Play } from "lucide-react";
import { SegmentedRunProgress } from "@/components/SegmentedRunProgress";
import { HardenedReauthPreview } from "@/components/HardenedReauthPreview";

export const SegmentedReadingPreview = ({ jobId, open, onClose }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [run, setRun] = useState(null);
  const [ownerCheck, setOwnerCheck] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [starting, setStarting] = useState(false);
  // P6I: presenza di run/auth hardened => il pannello legacy P1.7 NON viene
  // renderizzato in parallelo (solo consultazione dalla cronologia).
  const [hardenedPresent, setHardenedPresent] = useState(null);
  const [hardenedVersion, setHardenedVersion] = useState(null);

  useEffect(() => {
    if (!open || !jobId) return;
    setD(null); setErr(null); setRun(null); setHardenedPresent(null);
    setOwnerCheck(false); setPhrase("");
    api.get(`/reader/jobs/${jobId}/segmented-run/status`)
      .then(({ data }) => setRun(data)).catch(() => {});
    api.get(`/reader/jobs/${jobId}/segmented-preflight`, { timeout: 300000 })
      .then(({ data }) => setD(data))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail, { status: e.response?.status, network: !e.response })));
  }, [open, jobId]);

  const b = d?.budget;
  const auth = run?.authorization_present ? run.authorization : null;
  const startAvailable = auth && !auth.consumed && !auth.expired
    && run?.run_status === "AUTHORIZED_AWAITING_OWNER_START";
  const confirmsComplete = ownerCheck
    && phrase.trim() === (auth?.confirmation_phrase || "\u0000");

  const startRun = async () => {
    if (!confirmsComplete || starting) return;
    setStarting(true);
    try {
      const { data } = await api.post(`/reader/jobs/${jobId}/segmented-run/execute`,
        { confirm: true, confirmation_text: phrase.trim() }, { timeout: 300000 });
      toast.success(`Run segmentato avviato: ${data.logical_reader_run_id}`);
      const { data: st } = await api.get(`/reader/jobs/${jobId}/segmented-run/status`);
      setRun(st);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, { status: e.response?.status, network: !e.response }));
    } finally {
      setStarting(false);
    }
  };

  const legacyPanel = (
    <div className="space-y-2 min-w-0" data-testid="legacy-p17-panel">
        {auth && (
        <div className="min-w-0 border-2 border-emerald-500 bg-emerald-50 p-2 space-y-1.5" data-testid="segmented-start-box">
          <div className="font-bold text-emerald-900">Autorizzazione Owner one-shot predisposta — {run.state === "READY_FOR_OWNER_SEGMENTED_RUN_START" ? "READY_FOR_OWNER_SEGMENTED_RUN_START" : run.state}</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-3 gap-y-0.5 text-[11px] text-emerald-950">
            <div>Run: <span className="font-mono">{(run.logical_reader_run_id || "").slice(0, 13)}…</span></div>
            <div>Tetto autorizzato: <b data-testid="segmented-auth-cap">${auth.max_cost_usd}</b> (limite assoluto)</div>
            <div>Chiamate max: <b>{auth.max_calls}</b> (sequenziali, 1 POST/segmento)</div>
            <div>Tipico: ${auth.budget_frozen.typical_total_usd}</div>
            <div>Worst case: ${auth.budget_frozen.worst_case_total_usd} · margine {auth.budget_frozen.safety_margin_pct}%</div>
            <div>Tetto minimo: ${auth.budget_frozen.minimum_required_cap_usd}</div>
            <div>Scadenza: {auth.expires_at?.slice(0, 16)}</div>
            <div>Nessun retry automatico</div>
            <div>Esito obbligatorio: HUMAN_REVIEW</div>
          </div>
          {startAvailable && (
            <div className="border border-emerald-400 bg-white p-2 space-y-1.5">
              <label className="flex items-start gap-2 cursor-pointer">
                <Checkbox checked={ownerCheck} onCheckedChange={(v) => setOwnerCheck(!!v)} data-testid="segmented-owner-checkbox" className="mt-0.5" />
                <span>Confermo come Owner l'avvio manuale della lettura segmentata (max {auth.max_calls} chiamate, tetto assoluto ${auth.max_cost_usd}, nessun retry, esito in revisione umana)</span>
              </label>
              <Input value={phrase} onChange={(e) => setPhrase(e.target.value)}
                placeholder={`Digita esattamente: ${auth.confirmation_phrase}`}
                data-testid="segmented-confirmation-input" className="h-7 text-xs font-mono" />
              <button onClick={startRun} disabled={!confirmsComplete || starting}
                className="w-full px-2 py-1.5 border-2 border-emerald-700 bg-emerald-600 text-white font-bold text-xs flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-emerald-700"
                data-testid="segmented-start-run-btn">
                <Play size={12} />{starting ? "Avvio in corso…" : "Avvia lettura segmentata"}
              </button>
              <div className="text-[10px] text-muted-foreground">Il pulsante resta disabilitato finché la checkbox e la frase esatta non sono complete. Al clic l'autorizzazione one-shot viene consumata atomicamente.</div>
            </div>
          )}
          {auth.consumed && <div className="text-[11px] font-bold text-emerald-900" data-testid="segmented-auth-consumed">Autorizzazione consumata il {auth.consumed_at}: nessuna seconda esecuzione possibile.</div>}
          {auth.expired && !auth.consumed && <div className="text-[11px] font-bold text-red-800">Autorizzazione SCADUTA: serve una nuova decisione Owner.</div>}
        </div>
      )}
      {run?.authorization_present && (
        <SegmentedRunProgress jobId={jobId} status={run} onStatus={setRun} />
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent style={{ width: "min(95vw, 56rem)" }} className="max-w-[95vw] max-h-[90dvh] overflow-y-auto overflow-x-hidden text-xs p-3 sm:p-6 [grid-template-columns:minmax(0,1fr)]" data-testid="segmented-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm">
            <Layers size={15} />Anteprima lettura segmentata — {hardenedPresent ? (hardenedVersion || "quote_reader_P1.7.2_SEGMENTED_HARDENED_CANDIDATE") : (d?.reader_version_candidate || "quote_reader_P1.7_SEGMENTED_CANDIDATE")}
          </DialogTitle>
        </DialogHeader>
        {err && <div className="border border-red-300 bg-red-50 p-2 text-red-900" data-testid="segmented-preview-error">{err}</div>}

        <HardenedReauthPreview jobId={jobId} legacyPanel={legacyPanel} preflight={d}
          onLoaded={(a) => { setHardenedPresent(!!a?.hardened_present); if (a?.reader_version) setHardenedVersion(a.reader_version); }} />
        {hardenedPresent === false && legacyPanel}

        {!d && !err && <div className="py-6 text-center text-muted-foreground" data-testid="segmented-preview-loading">
          Dry-plan deterministico in corso (ZERO chiamate provider, solo lettura locale del PDF)…</div>}
        {d && (
          <div className="space-y-3 min-w-0 w-full" data-testid="segmented-preview-body">
            <div className="border border-border p-2 space-y-0.5">
              <div><b>Candidata:</b> {d.reader_version_candidate} · prompt {d.prompt_version} · <b>Baseline intatta:</b> {d.baseline_reader_untouched}</div>
              <div><b>Target:</b> {d.job.filename} · stato <span className="font-bold">{d.job.status}</span> · SHA <span className="font-mono">{(d.job.pdf_sha256 || "").slice(0, 16)}…</span></div>
              <div><b>Struttura:</b> {d.target_profile_summary.document_type} · pagine {d.target_profile_summary.received_pages}/{d.target_profile_summary.expected_pages} · codici distinta: {d.target_profile_summary.bom_codes.length}</div>
              <div><b>Stato:</b> <span className="font-bold" data-testid="segmented-preview-state">{run?.state || d.state}</span> · read-only · zero provider calls</div>
            </div>

            {d.missing_pages.length > 0 && (
              <div className="border-2 border-red-400 bg-red-50 p-2 text-red-900 font-bold flex gap-2" data-testid="segmented-preview-page8-warning">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>PACKAGE_PAGE_SEQUENCE_GAP — pagine dichiarate mancanti: [{d.missing_pages.join(", ")}]. <span className="font-normal">{d.missing_page_policy}</span></span>
              </div>
            )}

            <div className="border border-border overflow-x-auto">
              <table className="w-full text-[11px]" data-testid="segmented-preview-table">
                <thead><tr className="border-b border-border bg-muted text-left">
                  {["Segmento", "Componente", "Pagine", "Immagini", "Input cap (tok)", "Output cap (tok)", "Costo tipico", "Worst case"].map((h) =>
                    <th key={h} className="px-2 py-1.5 font-medium">{h}</th>)}
                </tr></thead>
                <tbody>
                  {(b?.per_segment || []).map((s) => (
                    <tr key={s.segment_id} className="border-b border-border" data-testid={`segment-row-${s.segment_id}`}>
                      <td className="px-2 py-1 font-bold">{s.segment_id} <span className="font-normal text-muted-foreground">({s.kind})</span></td>
                      <td className="px-2 py-1 font-mono">{s.component_code || "—"}</td>
                      <td className="px-2 py-1">{(d.dry_plan.segments.find((x) => x.segment_id === s.segment_id)?.pages || []).join(", ") || "—"}</td>
                      <td className="px-2 py-1">{s.n_images}</td>
                      <td className="px-2 py-1">{s.input_cap_tokens?.toLocaleString?.() ?? s.input_cap_tokens}</td>
                      <td className="px-2 py-1">{s.output_cap_tokens?.toLocaleString?.() ?? s.output_cap_tokens}</td>
                      <td className="px-2 py-1">${s.est_cost_typical_usd}</td>
                      <td className="px-2 py-1 font-bold">${s.worst_case_usd}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="border border-border p-2 space-y-0.5" data-testid="segmented-preview-costs">
                <div className="font-bold">Costi del logical run ({b?.max_calls} chiamate massime, modello {b?.model})</div>
                <div>Costo cumulativo tipico: <b>${b?.typical_total_usd}</b></div>
                <div>Worst case globale (riserva GLOBAL_WORST_CASE_RESERVED_COST): <b data-testid="segmented-preview-worst-case">${b?.worst_case_total_usd}</b></div>
                <div>Margine di sicurezza: <b>{b?.safety_margin_pct}%</b></div>
                <div>Tetto minimo necessario (worst × 1.25): <b data-testid="segmented-preview-min-cap">${b?.minimum_required_cap_usd}</b></div>
                {auth && <div>Tetto autorizzato Owner: <b>${auth.max_cost_usd}</b> (limite assoluto, mai aumentato automaticamente)</div>}
                <div className="text-muted-foreground">{b?.gate_rule}</div>
                <div className="text-muted-foreground">Token conservativi per immagine: {b?.image_tokens_conservative}</div>
              </div>
              <div className="border border-border p-2 space-y-0.5" data-testid="segmented-preview-gold">
                <div className="font-bold">Riferimenti GOLD (contesto, mai fatti del target)</div>
                {(d.gold_references.selected || []).map((r) => (
                  <div key={r.case_id}>· {r.case_id} — punteggio {r.score} (GOLD_VERIFIED)</div>
                ))}
                <div className="text-muted-foreground">ReferencePacketV1: <span className="font-mono">{(d.gold_references.reference_packet_hash || "").slice(0, 20)}…</span> (immutato)</div>
                <div className="text-muted-foreground">Immagini totali del run: {d.dry_plan.images_total_across_run} (duplicate tra segmenti: {d.dry_plan.images_duplicated_across_segments}) — mai tutte le immagini a ogni segmento</div>
              </div>
            </div>

            {!auth && !hardenedPresent && (
              <div className="border-2 border-amber-400 bg-amber-50 p-2 flex gap-2 items-start" data-testid="segmented-preview-no-auth">
                <Lock size={14} className="mt-0.5 shrink-0 text-amber-800" />
                <div className="text-amber-900">
                  <b>NESSUNA AUTORIZZAZIONE LEGACY P1.7 ATTIVA.</b> Il tetto reale del run viene fissato dall'Owner nel passaggio «Autorizza» P1.7.2 (riquadro in alto) sui numeri qui mostrati.
                  Il job originale resta {d.job.status}.
                </div>
              </div>
            )}

            {(d.dry_plan.segments || []).filter((s) => s.kind === "COMPONENT").map((s) => (
              <div key={s.segment_id} className="text-[10px] text-muted-foreground" data-testid={`segment-reason-${s.segment_id}`}>
                <b>{s.segment_id}</b> {s.component_code}: {s.association_reason}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
